import queryKeys from "@configs/react-query/query-key";
import { default as ToastConstant, default as useSuccessToast } from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useMutationData } from "@hooks/useMutationData";
import { STORES, updateDataInIndexedDb } from "@utils/indexedDb";
import { deduplicateComponents, normalizeComponentsWithSeq } from "@utils/common";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const useSbomMerge = () => {
  const [componentList, setComponentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get all license policies list
  const getLicensePolicyList = useMutationData(
    queryKeys.getListLicensePolicy,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string" && response) {
          return Array.isArray(response) ? response : (response.object || []);
        }
        return [];
      },
      onError: (error) => {
        console.error("Error fetching license policy list:", error);
        return [];
      },
    },
  );

  // Get component data based on verification type
  const getComponentData = useMutationData(
    (data) => {
      const { verificationType, projectId, verificationVersion } = data;
      let baseUrl;

      switch (verificationType) {
        case "FossId":
          baseUrl = queryKeys.getSourceCodeScanComponent;
          break;
        case "Clarity":
          baseUrl = queryKeys.getBinaryScanComponent;
          break;
        case "SBOM":
          baseUrl = queryKeys.getSbomScanComponent;
          break;
        case "clarity_air":
          baseUrl = queryKeys.getClarityAirScanComponentBySeq;
          break;
        case "ClarityAIR":
          baseUrl = queryKeys.getClarityAirScanComponentBySeq;
          break;
        default:
          console.error("Invalid verificationType provided:", verificationType);
          baseUrl = queryKeys.getSourceCodeScanComponent; // fallback
      }

      return `${baseUrl}?projectId=${projectId}&verificationVersion=${verificationVersion}`;
    },
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string" && response?.object) {
          return response.object;
        }
        return [];
      },
      onError: (error) => {
        console.error("Error fetching component data:", error);
        return [];
      },
    },
  );

  // Fetch components for all verification files
  const fetchComponentsForVerificationFiles = useCallback(
    async (files) => {
      if (!files || files.length === 0) {
        setComponentList([]);
        return [];
      }

      setLoading(true);
      try {
        const componentPromises = files.map(async (file) => {
          try {
            const response = await getComponentData.mutateAsync({
              verificationType: file.verificationType,
              projectId: file.projectId,
              verificationVersion: file.verificationVersion,
            });

            const componentList = Array.isArray(response) ? response : response?.object || [];

            // Preserve source verification context for frozen VEX scope (projectSeq, scanToolCd, etc.)
            return componentList.map((component) => ({
              ...component,
              realFileName: file.realFileName,
              verificationType: file.verificationType,
              verificationVersion: file.verificationVersion ?? component.verificationVersion,
              projectName: file.projectName,
              projectSeq: component.projectSeq ?? file.projectSeq,
              verificationSeq: component.verificationSeq ?? file.verificationSeq,
            }));
          } catch (error) {
            console.error(`Error fetching components for ${file.projectName}:`, error);
            return [];
          }
        });

        const allComponents = await Promise.all(componentPromises);
        const flattenedComponents = allComponents.flat();

        let licensePolicyMap = new Map();
        try {
          const licensePolicyList = await getLicensePolicyList.mutateAsync();
          if (Array.isArray(licensePolicyList) && licensePolicyList.length > 0) {
            licensePolicyList.forEach((policy) => {
              if (policy.licensePolicySeq) {
                licensePolicyMap.set(policy.licensePolicySeq, policy);
              }
            });
          }
        } catch (error) {
          console.error("Error fetching license policy list:", error);
        }

        const componentsWithLicensePolicy = flattenedComponents.map((component) => ({
          ...component,
          licensePolicy: component.licensePolicySeq 
            ? licensePolicyMap.get(component.licensePolicySeq) || null
            : null,
        }));

        const normalizedComponents = normalizeComponentsWithSeq(componentsWithLicensePolicy);
        const uniqueComponents = deduplicateComponents(normalizedComponents);

        setComponentList(uniqueComponents);
        return uniqueComponents;
      } catch (error) {
        console.error("Error fetching components:", error);
        ToastConstant(TOAST.error, t("common.failed-to-fetch-component-data"));
        return [];
      } finally {
        setLoading(false);
      }
    },
    [getComponentData, getLicensePolicyList, t],
  );

  // Create merged SBOM mutation
  const createSbomMerged = useMutationData(
    queryKeys.createSbomMerged,
    {
      method: "POST",
    },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.MERGED_SBOMS, response, response.mergedSbomSeq);
        useSuccessToast(TOAST.success, t("common.create-successfully"));
        navigate(`/sbom-mergeds`);
      },
      onError: (error) => {
        console.error("Error during create merged SBOM:", error);
        ToastConstant(TOAST.error, t("common.failed-to-create-merged-sbom"));
      },
    },
  );

  return {
    componentList,
    loading,
    fetchComponentsForVerificationFiles,
    createSbomMerged,
  };
};

export default useSbomMerge;
